/**
 * @file Breadcrumbs.jsx
 * @description The one breadcrumb trail for the dark-navy hero of every public
 *   SEO page (city pages, guides, tracker pages, resource hubs). Replaces the
 *   `Crumb` helper that was copy-pasted into 18 page components.
 *   Visual-only: each page still emits its own BreadcrumbList JSON-LD via
 *   `useBreadcrumbJsonLd` (lib/seoSchema), which this component does not touch.
 *   Mobile: every link is a 44px-tall tap target; the current page is plain text.
 * @module components/Breadcrumbs
 */
import { ChevronRightIcon } from "./Icons";

const LINK_COLOR = "#a5b4fc";      // indigo-300: 8:1 on the navy hero
const CURRENT_COLOR = "#e2e8f0";   // slate-200: readable "you are here"
const CHEVRON_COLOR = "rgba(255,255,255,0.6)";

/** Minimum tap height for a crumb link (px). Exported so tests pin the rule. */
export const CRUMB_MIN_HEIGHT = 44;

/**
 * @param {{ items: Array<{ label: string, onClick?: Function }>, ariaLabel?: string }} props
 *   The last item is the current page: it is never a link, whatever it carries.
 */
export default function Breadcrumbs({ items, ariaLabel = "Breadcrumb" }) {
  const lastIndex = items.length - 1;
  return (
    <nav
      aria-label={ariaLabel}
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, marginTop: -8, marginBottom: 8 }}
    >
      {items.map((item, i) => {
        const last = i === lastIndex;
        return (
          <span
            key={`${i}-${item.label}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 14, color: last ? CURRENT_COLOR : LINK_COLOR, fontWeight: last ? 600 : 500 }}
          >
            {item.onClick && !last ? (
              <button
                type="button"
                onClick={item.onClick}
                style={{
                  background: "none", border: "none", color: LINK_COLOR, cursor: "pointer",
                  fontSize: 14, fontWeight: 500, fontFamily: "inherit",
                  padding: "0 6px", minHeight: CRUMB_MIN_HEIGHT, display: "inline-flex", alignItems: "center",
                }}
              >
                {item.label}
              </button>
            ) : (
              <span aria-current={last ? "page" : undefined} style={{ display: "inline-flex", alignItems: "center", minHeight: CRUMB_MIN_HEIGHT, padding: "0 6px" }}>
                {item.label}
              </span>
            )}
            {!last && <ChevronRightIcon size={12} color={CHEVRON_COLOR} />}
          </span>
        );
      })}
    </nav>
  );
}
